//*Libraries imports
import { useEffect, useState } from "react";
import { Download, AlertCircle, Loader2 } from "lucide-react";
import { useOutletContext } from "react-router-dom";

//* Components imports
import { PaymentInnerHeader } from "./components/shared/PaymentInnerHeader";
import { SalesContractArea } from "./components/shared/SalesContractArea";
import { ProductInfo } from "./components/shared/ProductInfo";
import {
  BankSlipItem,
  BankSlipData,
} from "./components/bank-slip/BankSlipItem";

//* Hooks Imports
import { useCreatePayment } from "@/hooks/asaas/payments/mutations/useCreatePayment";

//*Types Imports
import { ISaleOutputDTO } from "@/schemas/sales";
import { PaymentResponseAsaasDTO } from "@/schemas/asaas/payment";

//* Helpers Imports
import { getSalePrice } from "@helpers/saleHelper";
import { calculatePaymentValues } from "@helpers/paymentCalculator";
import { MethodPaymentEnum } from "@/schemas/methodpayment";

//* Utils Imports
import { isSalePaid } from "./components/shared/utils";

export function PaymentBankSlip() {
  const { sale, refetchSale } = useOutletContext<{
    sale: ISaleOutputDTO | null;
    refetchSale: (id: string) => Promise<any>;
  }>();
  const mutateCreatePayment = useCreatePayment();
  const [payment, setPayment] = useState<PaymentResponseAsaasDTO | null>(null);

  const isPaid = isSalePaid(sale);

  const bankSlipData: BankSlipData | null = (() => {
    if (payment && "bankSlipUrl" in payment && payment.bankSlipUrl) {
      return {
        id: payment.id,
        dueDate: payment.dueDate,
        value: payment.value,
        bankSlipUrl: payment.bankSlipUrl,
        status: payment.status,
      };
    }

    const details = sale?.paymentDetails;
    const asaasId = details?.asaasId || details?.asaasPaymentId;
    const method = details?.method || details?.billingType;
    const bankSlipUrl = details?.bankSlipUrl;

    if (asaasId && (method === "BOLETO" || method === "boleto")) {
      return {
        id: asaasId,
        dueDate: details.dueDate,
        value: details.originalValue || getSalePrice(sale),
        bankSlipUrl: bankSlipUrl,
        status: details.status || sale?.paymentStatus || "PENDING",
      };
    }

    return null;
  })();

  useEffect(() => {
    const checkExistingPayment = () => {
      if (!sale) return;

      const details = sale.paymentDetails;
      const asaasId = details?.asaasId || details?.asaasPaymentId;
      const method = details?.method || details?.billingType;

      if (asaasId && method === "BOLETO") {
        setPayment({
          id: asaasId,
          bankSlipUrl: details.bankSlipUrl,
          dueDate: details.dueDate,
          value: details.originalValue || getSalePrice(sale),
          status: details.status || sale.paymentStatus,
          billingType: "BOLETO",
        } as any);
      }
    };

    checkExistingPayment();
  }, [sale]);

  const handleGenerateBoleto = async () => {
    if (!sale || !sale._id) return;

    try {
      const customerId =
        typeof sale.clientId === "object"
          ? sale.clientId.customerId
          : sale.clientId;

      if (!customerId) return;

      const calc = calculatePaymentValues({
        sale,
        billingType: MethodPaymentEnum.BOLETO,
      });

      mutateCreatePayment.mutate(
        {
          saleId: sale._id,
          customer: customerId as string,
          billingType: "BOLETO",
          value: calc.finalValue,
          installmentCount: 1,
          totalValue: calc.finalValue,
        },
        {
          onSuccess: (response) => {
            if (response) {
              setPayment(response);
              if (sale._id) refetchSale(sale._id);
            }
          },
          onError: (err: any) => {
            if (
              err.code === "PaymentValueMismatch" ||
              err.message?.includes("desatualizados")
            ) {
              window.location.reload();
              return;
            }
            console.error("Erro ao criar pagamento:", err);
          },
        },
      );
    } catch (err) {
      console.error("Erro ao criar pagamento:", err);
    }
  };

  const handleDownloadAll = () => {
    if (bankSlipData?.bankSlipUrl) {
      window.open(bankSlipData.bankSlipUrl, "_blank");
    }
  };

  const getProductName = (product: any) => {
    if (!product) return "Produto não encontrado";
    if (typeof product === "object") return product.name;
    const match = product.match(/name:\s*'([^']+)'/);
    return match ? match[1] : product;
  };

  const paymentValues = sale
    ? calculatePaymentValues({
        sale,
        billingType: MethodPaymentEnum.BOLETO,
      })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center">
      <div className="w-full max-w-[800px] flex flex-col bg-white shadow-sm min-h-screen relative">
        <div className="sticky top-0 z-20 bg-white">
          <PaymentInnerHeader title="Boletos" />

          <SalesContractArea />

          <div className="px-4 pb-4 flex flex-col gap-4 border-b border-gray-100">
            {isPaid ? (
              <div className="bg-[#FCFCFC] rounded-[18px] p-6 border border-[#EDF2F6] flex flex-col items-center gap-4 text-center">
                <div className="size-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                  <AlertCircle className="size-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-clarotexto-201">
                  Pagamento confirmado!
                </h2>
                <p className="text-gray-500 max-w-sm">
                  O pagamento deste pedido já foi realizado com sucesso. Você
                  não precisa fazer mais nada.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-[#FCFCFC] rounded-[18px] p-4 border border-[#EDF2F6] flex flex-col gap-4">
                  <h2 className="text-lg font-bold text-clarotexto-201">
                    Boleto
                  </h2>
                  <ProductInfo
                    title={
                      sale ? getProductName(sale.productId) : "Carregando..."
                    }
                    price={
                      paymentValues ? (
                        <div className="flex flex-col items-end">
                          {paymentValues.discountApplied && (
                            <span className="text-sm text-gray-400 line-through">
                              {new Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              }).format(paymentValues.originalValue)}
                            </span>
                          )}
                          <span className="text-xl font-bold text-claroprimaria-01">
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(paymentValues.finalValue)}
                          </span>
                          {paymentValues.discountApplied && (
                            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mt-1">
                              {paymentValues.discountApplied}%{" "}
                              {sale?.managerDiscount
                                ? "de desconto aplicado"
                                : "de desconto à vista"}
                            </span>
                          )}
                        </div>
                      ) : (
                        "..."
                      )
                    }
                  />
                </div>

                {bankSlipData ? (
                  <button
                    onClick={handleDownloadAll}
                    className="w-full h-[42px] mt-2 flex items-center justify-center gap-2 bg-gray-50 border border-gray-200 rounded-lg text-claroprimaria-01 font-semibold hover:bg-gray-100 transition-colors"
                  >
                    <Download className="size-5" />
                    Baixar boleto
                  </button>
                ) : (
                  <button
                    onClick={handleGenerateBoleto}
                    disabled={mutateCreatePayment.isPending}
                    className="w-full h-[42px] mt-2 flex items-center justify-center gap-2 bg-claroprimaria-01 text-white rounded-lg font-semibold hover:bg-claroprimaria-02 transition-colors disabled:opacity-50"
                  >
                    {mutateCreatePayment.isPending ? (
                      <>
                        <Loader2 className="size-5 animate-spin" />
                        Gerando...
                      </>
                    ) : (
                      "Gerar boleto"
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 pb-[180px]">
          {mutateCreatePayment.isPending ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-500">
              <Loader2 className="size-8 animate-spin text-claroprimaria-01" />
              <p className="font-medium">Gerando seu boleto...</p>
            </div>
          ) : mutateCreatePayment.error ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-red-500 bg-red-50 rounded-2xl border border-red-100 mx-2">
              <AlertCircle className="size-8" />
              <p className="font-medium text-center px-6">
                {mutateCreatePayment.error.message}
              </p>
            </div>
          ) : bankSlipData ? (
            <BankSlipItem item={bankSlipData} />
          ) : (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
              <p className="text-center px-8">
                Clique no botão acima para gerar seu boleto de pagamento.
              </p>
            </div>
          )}
        </div>

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] bg-white border-t border-gray-200 p-6 pb-8 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <h3 className="text-sm font-bold text-gray-700 mb-1">Regras:</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Baixe o boleto acima apresentado e salve para pagamento na data
              estabelecida. Esta página após baixar o boleto não ficará
              disponível.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
